/* eslint-disable react-hooks/exhaustive-deps */

import { useRef, useEffect, useState, useCallback } from "react";
import { Play, Pause, Printer, Share2, MousePointer2 } from "lucide-react";
import { Slider } from "./ui/slider";
import { asset } from "../lib/api";

const parseMin = (s) => {
  if (!s) return null;
  const m = s.match(/(\d+):(\d+)\s*(AM|PM)?/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ap = (m[3] || "").toUpperCase();
  if (ap === "PM" && h !== 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return h * 60 + min;
};
const fmtMin = (t) => {
  t = ((t % 1440) + 1440) % 1440;
  let h = Math.floor(t / 60);
  const m = Math.round(t % 60);
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ap}`;
};

export const ScrollScrubViewer = ({ frames, title, location, startLabel, endLabel, initialFrame = 0, onOrder, onShare }) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const imgsRef = useRef([]);
  const loadedRef = useRef(0);
  const targetRef = useRef(0);
  const curRef = useRef(0);
  const rafRef = useRef(null);
  const playRafRef = useRef(null);
  const playingRef = useRef(false);
  const lastIdxRef = useRef(-1);
  const vhRef = useRef(typeof window !== "undefined" ? window.innerHeight : 800);
  const touchYRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [pct, setPct] = useState(0);
  const [progress, setProgress] = useState(0);
  const [index, setIndex] = useState(initialFrame);
  const [playing, setPlaying] = useState(false);

  const total = frames.length;
  const startMin = parseMin(startLabel);
  const endMin = parseMin(endLabel);
  const tsFor = (idx) => {
    if (startMin == null || endMin == null) return `Frame ${idx + 1}`;
    let end = endMin < startMin ? endMin + 1440 : endMin;
    const f = total > 1 ? idx / (total - 1) : 0;
    return fmtMin(startMin + (end - startMin) * f);
  };

  // preload
  useEffect(() => {
    imgsRef.current = new Array(total);
    loadedRef.current = 0;
    setReady(false);
    setPct(0);
    let mounted = true;
    frames.forEach((url, i) => {
      const img = new Image();
      img.decoding = "async";
      const done = () => {
        if (!mounted) return;
        loadedRef.current += 1;
        setPct(Math.round((loadedRef.current / total) * 100));
        if (loadedRef.current >= Math.min(total, 8)) setReady(true);
      };
      img.onload = done;
      img.onerror = done;
      img.src = asset(url);
      imgsRef.current[i] = img;
    });
    return () => { mounted = false; };
  }, [frames]);

  const draw = useCallback((idx) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let img = imgsRef.current[idx];
    if (!img || !img.complete || !img.naturalWidth) {
      for (let r = 1; r < total; r++) {
        const a = imgsRef.current[idx - r];
        const b = imgsRef.current[idx + r];
        if (a && a.complete && a.naturalWidth) { img = a; break; }
        if (b && b.complete && b.naturalWidth) { img = b; break; }
      }
    }
    if (!img || !img.naturalWidth) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cw = canvas.clientWidth, ch = canvas.clientHeight;
    if (canvas.width !== Math.round(cw * dpr) || canvas.height !== Math.round(ch * dpr)) {
      canvas.width = Math.round(cw * dpr);
      canvas.height = Math.round(ch * dpr);
    }
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const ir = img.naturalWidth / img.naturalHeight, cr = cw / ch;
    let dw, dh, dx, dy;
    if (ir > cr) { dh = ch; dw = ch * ir; dx = (cw - dw) / 2; dy = 0; }
    else { dw = cw; dh = cw / ir; dx = 0; dy = (ch - dh) / 2; }
    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(img, dx, dy, dw, dh);
  }, [total]);

  // rAF render loop
  useEffect(() => {
    const loop = () => {
      curRef.current += (targetRef.current - curRef.current) * 0.16;
      const idx = Math.max(0, Math.min(total - 1, Math.round(curRef.current)));
      draw(idx);
      if (idx !== lastIdxRef.current) { lastIdxRef.current = idx; setIndex(idx); }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [draw, total]);

  // scroll -> frame
  useEffect(() => {
    const onScroll = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const dist = wrap.offsetHeight - vhRef.current;
      const p = Math.max(0, Math.min(1, -rect.top / dist));
      setProgress(p);
      targetRef.current = p * (total - 1);
    };
    const onResize = () => { vhRef.current = window.innerHeight; onScroll(); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    onScroll();
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onResize); };
  }, [total]);

  const scrollToFrame = (f) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const dist = wrap.offsetHeight - vhRef.current;
    const top = wrap.offsetTop + (f / (total - 1)) * dist;
    window.scrollTo({ top, behavior: "auto" });
  };

  useEffect(() => {
    if (ready && initialFrame > 0) scrollToFrame(initialFrame);
    // eslint-disable-next-line
  }, [ready]);

  const stopPlay = () => { playingRef.current = false; setPlaying(false); if (playRafRef.current) cancelAnimationFrame(playRafRef.current); };
  const play = () => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    playingRef.current = true; setPlaying(true);
    const dist = wrap.offsetHeight - vhRef.current;
    const startTop = wrap.offsetTop;
    const startP = progress >= 0.98 ? 0 : progress;
    const dur = Math.max(4000, total * 70);
    const t0 = performance.now();
    const step = (t) => {
      const e = Math.min(1, (t - t0) / dur);
      const pr = startP + (1 - startP) * e;
      window.scrollTo({ top: startTop + pr * dist });
      if (e < 1 && playingRef.current) playRafRef.current = requestAnimationFrame(step);
      else stopPlay();
    };
    playRafRef.current = requestAnimationFrame(step);
  };

  const onTouchStart = (e) => { touchYRef.current = e.touches[0].clientY; stopPlay(); };
  const onTouchMove = (e) => {
    if (touchYRef.current == null) return;
    const y = e.touches[0].clientY;
    window.scrollBy(0, (touchYRef.current - y) * 2.2);
    touchYRef.current = y;
  };

  const capture = () => {
    const idx = Math.max(0, Math.min(total - 1, Math.round(curRef.current)));
    const img = imgsRef.current[idx];
    if (!img || !img.naturalWidth) return null;
    const off = document.createElement("canvas");
    off.width = img.naturalWidth;
    off.height = img.naturalHeight;
    off.getContext("2d").drawImage(img, 0, 0);
    try { return off.toDataURL("image/jpeg", 0.95); } catch (e) { return null; }
  };

  const handleOrder = () => {
    const dataUrl = capture();
    onOrder && onOrder({ index, dataUrl, timestamp: tsFor(index) });
  };

  return (
    <div ref={wrapRef} data-testid="scrub-wrapper" style={{ height: "340vh" }} className="relative">
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-black">
        <canvas
          ref={canvasRef}
          data-testid="scroll-scrub-canvas"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${ready ? "opacity-100" : "opacity-0"}`}
        />
        {/* cinematic vignette */}
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(120% 90% at 50% 40%, transparent 55%, rgba(0,0,0,0.55) 100%)" }} />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/70 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-52 bg-gradient-to-t from-black/85 to-transparent" />

        {/* loading */}
        {!ready && (
          <div data-testid="scrub-loading" className="absolute inset-0 flex flex-col items-center justify-center bg-obsidian z-20">
            <div className="overline-label mb-4">Preloading frames</div>
            <div className="w-56 h-[3px] bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-amber-glow transition-all duration-200" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-3 font-mono text-xs text-neutral-500">{pct}%</div>
          </div>
        )}

        {/* HUD top-left */}
        <div className="absolute top-24 left-6 lg:left-10 z-20 max-w-md">
          <div className="overline-label mb-2">{location}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-none">{title}</h1>
        </div>

        {/* HUD timestamp top-right */}
        <div className="absolute top-24 right-6 lg:right-10 z-20 text-right">
          <div className="font-mono text-2xl text-amber-glow tracking-widest">{tsFor(index)}</div>
          <div className="font-mono text-[0.7rem] text-neutral-400 mt-1">
            FRAME {String(index + 1).padStart(4, "0")} / {String(total).padStart(4, "0")}
          </div>
        </div>

        {/* scroll hint */}
        {ready && progress < 0.03 && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-40 z-20 flex flex-col items-center text-neutral-300">
            <span className="overline-label mb-2">Scroll to scrub</span>
            <MousePointer2 size={18} className="animate-scroll-hint" />
          </div>
        )}

        {/* bottom controls */}
        <div className="absolute inset-x-0 bottom-0 z-30 p-6 lg:px-10 lg:pb-8">
          <div className="mb-4">
            <div className="h-[3px] bg-white/12 rounded-full overflow-hidden">
              <div data-testid="scrub-progress" className="h-full bg-amber-glow" style={{ width: `${progress * 100}%` }} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <button
              data-testid="scrub-play-toggle"
              onClick={() => (playing ? stopPlay() : play())}
              className="w-11 h-11 rounded-full glass flex items-center justify-center hover:text-amber-glow transition-colors"
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? <Pause size={18} /> : <Play size={18} />}
            </button>
            <div className="flex-1 min-w-[160px] max-w-md">
              <Slider
                data-testid="scrub-slider"
                value={[index]}
                min={0}
                max={total - 1}
                step={1}
                onValueChange={(v) => { stopPlay(); scrollToFrame(v[0]); }}
              />
            </div>
            <button
              data-testid="scrub-share-btn"
              onClick={() => onShare && onShare(index)}
              className="h-11 px-4 rounded-full glass flex items-center gap-2 text-sm hover:text-amber-glow transition-colors"
            >
              <Share2 size={16} /> <span className="hidden sm:inline">Share frame</span>
            </button>
            <button
              data-testid="order-frame-print-btn"
              onClick={handleOrder}
              className="h-11 px-5 rounded-full btn-amber flex items-center gap-2 text-sm"
            >
              <Printer size={16} /> Order this frame as a print
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
